import { LOADING_LICENSES } from '@/constants';
import * as dataSelectors from '@/data/data-selectors';
import { LicenseSkeleton } from '@/placeholders';
import { LicensePlaceholder } from '@/placeholders/license-placeholder';
import { ViolationSummaryNotes } from '@/property-details/components/content/violation-summary-notes';
import * as pd from '@/property-details/components/property-details-content-styles';
import * as userSelectors from '@/user/user-selectors';
import { useLicenses } from '@/utils/hooks/data/useLicenses';
import { usePropertyDetails } from '@/utils/hooks/data/usePropertyDetails';
import { LoadingPlaceholder } from '@/widgets/components/data-loading-placeholder';
import { renderIf } from 'deckard-ui/dist/components/RenderIf/RenderIf';
import { set } from 'immutable';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ExpandableLicense } from './expandable-license/expandable-license';
import { StreetAddress as LCStreetAddress } from './license-content-styles';
import { TMultiLicenseDisplayConfig } from './license-header/types';
import { LicenseUpgradeable } from './license-upgradeable';
import { MultiLicenseDataDisplay } from './multi-license-data-display/multi-license-data-display';

const StreetAddress = styled(LCStreetAddress)`
  margin: 0;
`;

function hasStreetAddress({ streetAddress }: { streetAddress: unknown }) {
  return Boolean(streetAddress);
}

const MaybeStreetAddress = renderIf(hasStreetAddress, StreetAddress);

const LicenseDiv = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

function LicensesList({ refresh }: { refresh: () => void }) {
  const [expandedLicenses, setExpandedLicenses] = useState([]);

  const licenses = useSelector(dataSelectors.licensesData) as unknown[];

  const handleExpand = useCallback(
    (index) => {
      setExpandedLicenses(
        set(expandedLicenses, index, !expandedLicenses[index])
      );
    },
    [expandedLicenses]
  );

  return (
    <div className="w-full flex flex-col gap-10">
      {licenses.map((license: unknown, idx: number) => (
        <ExpandableLicense
          // @ts-expect-error Untyped state
          key={`${license.status}-${idx}`}
          index={idx}
          // @ts-expect-error Untyped state
          license={license}
          isExpanded={expandedLicenses[idx]}
          handleExpand={handleExpand}
          refresh={refresh}
        />
      ))}
    </div>
  );
}

function LicenseModuleSwitcher({ refresh }: { refresh: () => void }) {
  const userRights = useSelector(userSelectors.userRights);
  const tabs = useSelector(dataSelectors.propertyDetailsTabs);

  const { licenses, loading, error } = useLicenses();

  if (tabs?.license?.upgradeRequired || !userRights.viewLicense)
    return <LicenseUpgradeable />;

  if (loading) {
    return <LoadingPlaceholder loadingText={LOADING_LICENSES} />;
  }

  if (error) {
    return <div>Error loading licenses</div>;
  }

  if (!licenses?.length) {
    // Fallback case in the old codebase.
    return <LicensePlaceholder />;
  }

  return <LicensesList refresh={refresh} />;
}

const licenseTabAddNoteQueryParam = '?context=license';

export function LicenseContent() {
  const multiLicenseDisplay: TMultiLicenseDisplayConfig = useSelector(
    dataSelectors.getFromPropertyDetailsTabs(
      'license.config.multiLicenseDisplay'
    )
  );

  const { currentProperty, loading: propertyLoading } = usePropertyDetails();

  const refresh = useCallback(() => {
    // Transitional solution to refresh the page when license data is updated with edit all fields
    window.location.reload();
  }, []);

  if (propertyLoading) return <LicenseSkeleton />;

  return (
    <pd.Wrapper data-testid="property-details-license-view">
      <pd.DetailsWrapper>
        <LicenseDiv>
          <MaybeStreetAddress streetAddress={currentProperty?.address}>
            {currentProperty?.address}
          </MaybeStreetAddress>

          {/*
            Given that the component is meant to display information at the individual property level,
            we are utilizing "currentProperty" as the data store. "currentProperty" sources its
            data from the "/properties/<DECKARD_ID>" API, which operates on the individual property level.

            Whereas the "currentPropertyFromList" (from the "/properties" API) is data at the jurisdiction
            level is more suited for the views that pertain to the a wide scope, such as the map view.
          */}
          <MultiLicenseDataDisplay
            dataStore={currentProperty}
            multiLicenseDisplay={multiLicenseDisplay}
          />

          <LicenseModuleSwitcher refresh={refresh} />
        </LicenseDiv>

        <pd.Spacer />

        <ViolationSummaryNotes
          listings={currentProperty.listings}
          licenseTabParam={licenseTabAddNoteQueryParam}
        />
      </pd.DetailsWrapper>
    </pd.Wrapper>
  );
}
